import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Scatter, XAxis, YAxis } from 'recharts';
import type { StaticSummaryGroupItem } from './shared/StaticSummaryGroup';
import StaticSummaryGroup from './shared/StaticSummaryGroup';
import type { SartStatistics } from './shared/utils';
import { useFullscreen } from '@/components/layouts/StackedLayout/StackedLayout';

const seriesColor: Record<string, string> = {
    correct: 'var(--color-teal-600)',
    incorrect: 'var(--color-red-500)',
    missed: 'var(--color-amber-500)',
};

interface SARTResultProps {
    statistics: SartStatistics;
    questionLength: number;
    summaryFormation?: number[];
}

const SartResult = ({ statistics, questionLength, summaryFormation = [2, 3] }: SARTResultProps) => {
    const { isMobile } = useFullscreen();
    const { t } = useTranslation('assessmentResultManagementPage');

    const summaryItems: StaticSummaryGroupItem[] = useMemo(
        () => [
            {
                title: t('details.sart.percent', { percent: statistics.accuracy }),
                description: t('details.sart.accuracy'),
            },
            {
                title: t('details.sart.ms', { time: statistics.averageResponseTimeMs.toFixed(0) }),
                description: t('details.sart.averageTime'),
            },
            {
                title: t('details.sart.ms', { time: statistics.fastestResponseTimeMs }),
                description: t('details.sart.fastestTime'),
            },
        ],
        [statistics, t]
    );

    return (
        <div>
            <div className="mx-auto grid max-w-3xl gap-5 lg:gap-[30px]">
                <div className="w-full">
                    <StaticSummaryGroup formation={summaryFormation} isMobile={isMobile} items={summaryItems} />
                </div>

                {/* Results */}
                <div className="flex min-h-[50vh] w-full flex-col items-center justify-center">
                    <div className="h-full w-full">
                        <ResponsiveContainer width="100%" height={500}>
                            <LineChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="1 1" />
                                <XAxis
                                    padding="no-gap"
                                    dataKey="index"
                                    type="number"
                                    allowDuplicatedCategory={false}
                                    domain={[1, questionLength]}
                                    stroke="var(--color-slate-300)"
                                    tick={{ fill: 'var(--color-neutral-500)', fontSize: 12 }}
                                    label={{
                                        value: t('details.visualReaction.xaxisLineName'),
                                        position: 'insideBottom',
                                        offset: -15,
                                        fontSize: '12px',
                                    }}
                                />
                                <YAxis
                                    orientation="left"
                                    includeHidden
                                    dataKey="responseTimeMs"
                                    stroke="var(--color-slate-300)"
                                    tick={{ fill: 'var(--color-neutral-500)', fontSize: 12 }}
                                    label={{
                                        value: t('details.visualReaction.yaxisLineName'),
                                        angle: -90,
                                        position: 'insideLeft',
                                        offset: -5,
                                        dy: 40,
                                        fontSize: '12px',
                                    }}
                                />
                                <Legend
                                    className="mt-8 pt-8"
                                    iconSize={8}
                                    layout="vertical"
                                    align={isMobile ? 'center' : 'right'}
                                    verticalAlign={isMobile ? 'bottom' : 'middle'}
                                    wrapperStyle={
                                        isMobile
                                            ? {
                                                  fontSize: 14,
                                                  width: '100%',
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  alignItems: 'flex-start',
                                                  paddingTop: 40,
                                              }
                                            : {
                                                  right: 0,
                                                  top: 10,
                                                  fontSize: 14,
                                                  lineHeight: '1.75',
                                              }
                                    }
                                    formatter={value => <span className="ml-2 text-neutral-500">{value}</span>}
                                />
                                {statistics.series.map(s => (
                                    <Fragment key={`frag-${s.name}`}>
                                        <Scatter
                                            name={t(`details.sart.${s.name}`)}
                                            key={`scatter-${s.name}`}
                                            fill={seriesColor[s.name] || 'gray'}
                                            // Set dot size to match legend iconSize (8)
                                            legendType="circle"
                                            shape={(props: { cx?: number | string; cy?: number | string }) => (
                                                <circle
                                                    cx={props.cx}
                                                    cy={props.cy}
                                                    r={4} // iconSize=8 => r=4
                                                    fill={seriesColor[s.name] || 'gray'}
                                                />
                                            )}
                                        />
                                        <Line
                                            dataKey="responseTimeMs"
                                            data={s.data}
                                            name={t(`details.sart.${s.name}`)}
                                            key={`line-${s.name}`}
                                            strokeWidth="0"
                                            dot={{
                                                fill: seriesColor[s.name] || 'gray',
                                                strokeWidth: 0,
                                                stroke: seriesColor[s.name] || 'gray',
                                                r: 4, // iconSize=8 => r=4
                                            }}
                                            legendType="none"
                                        />
                                    </Fragment>
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default SartResult;
